import { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuthentication, useDialog, useReviews } from "hooks";
import { rateLimitService } from "services";
import { API_ENDPOINTS } from "constants";
import { ReviewListItem } from "./list-item";
import { ReviewInput } from "./input";
import { ReviewsMessage } from "./message";
import { ReviewListSkeleton } from "./list-skeleton";
import { SORT_OPTIONS, REPORT_REASONS } from "constants";

const useAuthAction = () => {
  const { user } = useAuthentication();
  const { confirm } = useDialog();

  const requireAuth = async (action) => {
    if (!user) {
      const result = await confirm({
        title: "Sign in required",
        description: `Sign in to ${action}?`,
        confirmLabel: "Sign in",
        cancelLabel: "Cancel",
      });
      if (result) window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
      return false;
    }
    return true;
  };

  return { requireAuth, user };
};

const useReviewActions = () => {
  const { confirm, input } = useDialog();

  const confirmDelete = (type = "review") =>
    confirm({
      title: `Delete ${type}`,
      description: `Are you sure you want to delete this ${type}?`,
      confirmLabel: "Yes",
      cancelLabel: "No",
    });

  const getReportData = () =>
    input({
      title: "Report Review",
      description: "Help us understand what's wrong with this review.",
      fields: [
        {
          name: "reason",
          label: "Reason",
          type: "select",
          defaultValue: "",
          required: true,
          options: REPORT_REASONS,
        },
        {
          name: "description",
          label: "Additional details (optional)",
          type: "text",
          multiline: true,
          minRows: 3,
          maxLength: 100,
        },
      ],
    });

  return { confirmDelete, getReportData };
};

export const ReviewList = ({
  deal,
  type = "deal",
  hideCreateButton = false,
  showUserReviewsOnly = false,
  sortBy,
  userId,
  contentFilter,
  isOwner,
}) => {
  const { select, history } = useDialog();
  const { requireAuth, user } = useAuthAction();
  const { confirmDelete, getReportData } = useReviewActions();

  const [formData, setFormData] = useState({
    dealId: type === "deal" ? deal?.id : null,
    dealTitle: type === "deal" ? deal?.title : null,
    description: "",
    rating: 0,
    restaurantId: deal?.restaurantId || null,
    restaurantName: deal?.restaurantName || null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [sortOption, setSortOption] = useState(sortBy || "newest");
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [fetchingLimit, setFetchingLimit] = useState(false);

  const {
    reviews,
    loading,
    error,
    createReview,
    updateReview,
    deleteReview,
    deleteReviewDraft,
    likeReview,
    dislikeReview,
    reportReview,
  } = useReviews({
    id: showUserReviewsOnly ? undefined : deal.id,
    type,
    userId,
    contentFilter,
    sortBy: sortOption,
  });

  const approvedReviewsCount = showUserReviewsOnly
    ? reviews.length
    : reviews.filter((r) => ["approved", "pendingEdit", "pendingDelete", "pendingReport"].includes(r.status)).length;

  const hasReviewed = reviews?.some((r) => r.userId === user?.uid);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: `${user.firstName} ${user.lastName}`,
        userId: user.uid,
        avatar: user.photoUrl || null,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;

    const syncReviewLimit = async () => {
      setFetchingLimit(true);
      const limits = await rateLimitService.getRateLimit(user.uid);
      setFetchingLimit(false);

      const lastReview = limits?.lastReviewTime
        ? limits.lastReviewTime?.toDate
          ? limits.lastReviewTime.toDate().getTime()
          : new Date(limits.lastReviewTime).getTime()
        : 0;

      if (lastReview > 0) {
        const calculate = () => {
          const diff = 300000 - (Date.now() - lastReview);
          return diff > 0 ? Math.ceil(diff / 1000) : 0;
        };

        setSecondsRemaining(calculate());
        const interval = setInterval(() => {
          const remaining = calculate();
          setSecondsRemaining(remaining);
          if (remaining <= 0) clearInterval(interval);
        }, 1000);

        return () => clearInterval(interval);
      }
    };

    syncReviewLimit();
  }, [user?.uid]);

  const handleSortClick = async () => {
    const result = await select({
      title: "Sort Reviews",
      options: SORT_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
      selectedValue: sortOption,
    });
    if (result) setSortOption(result.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await requireAuth("write a review"))) return;
    if (!formData.rating || formData.description.length > 250) return;

    setIsSubmitting(true);
    const result = await createReview(formData);
    if (result.success) {
      setFormData({
        dealId: deal.id,
        dealTitle: deal.title,
        description: "",
        rating: 0,
        name: `${user.firstName} ${user.lastName}`,
        restaurantId: deal.restaurantId,
        restaurantName: deal.restaurantName,
        userId: user.uid,
        avatar: user.photoUrl || null,
      });
    }
    setIsSubmitting(false);
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    const contentToEdit =
      review.status === "pendingEdit" && review.editDraft
        ? review.editDraft
        : { dealTitle: review.dealTitle, description: review.description, rating: review.rating };
    setEditData(contentToEdit);
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const handleEditSubmit = async (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    const result = await updateReview(reviewId, editData, review.status);
    if (result.success) { setEditingId(null); setEditData({}); }
  };

  const handleDelete = async (reviewId) => {
    const review = reviews.find((r) => r.id === reviewId);
    if (await confirmDelete()) await deleteReview(review);
  };

  const handleDeleteDraft = async (reviewId) => {
    if (await confirmDelete("review draft")) await deleteReviewDraft(reviewId);
  };

  const handleLike = async (reviewId, uid) => {
    if (await requireAuth("like this review")) await likeReview(reviewId, uid);
  };

  const handleDislike = async (reviewId, uid) => {
    if (await requireAuth("dislike this review")) await dislikeReview(reviewId, uid);
  };

  const handleReport = async (reviewId, uid) => {
    if (await requireAuth("report this review")) {
      const reportData = await getReportData();
      if (reportData) await reportReview(reviewId, uid, reportData);
    }
  };

  const handleHistory = (review) => history({ review });

  if (error) {
    return (
      <ReviewsMessage
        message="Failed to load reviews"
        icon={<ErrorIcon />}
        type="error"
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleSortClick}
          disabled={reviews.length < 1}
          sx={{ borderRadius: 3, textTransform: "none" }}
        >
          Sort by: {SORT_OPTIONS.find((opt) => opt.value === sortOption)?.label}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {approvedReviewsCount} Review{approvedReviewsCount !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {loading ? (
        <ReviewListSkeleton />
      ) : !reviews || reviews.length === 0 ? (
        <ReviewsMessage
          message={
            "No reviews yet. " +
            (isOwner
              ? "Owners cannot review their own restaurant."
              : showUserReviewsOnly
              ? "Go and explore!"
              : "Be the first to review!")
          }
        />
      ) : (
        <Box>
          {reviews.map((review) => (
            <ReviewListItem
              key={review.id}
              review={review}
              user={user}
              editingId={editingId}
              editData={editData}
              onStartEdit={startEdit}
              onCancelEdit={cancelEdit}
              onEditChange={setEditData}
              onEditSubmit={handleEditSubmit}
              onDelete={handleDelete}
              onDeleteDraft={handleDeleteDraft}
              onLike={handleLike}
              onDislike={handleDislike}
              onReport={handleReport}
              onHistory={handleHistory}
              showUserReviewsOnly={showUserReviewsOnly}
              restaurant={deal}
            />
          ))}
        </Box>
      )}

      {!isOwner && !hasReviewed && !hideCreateButton && (
        <Box mt={reviews.length > 0 ? 1 : null}>
          <Typography variant="subtitle1" gutterBottom>
            Leave a Review
          </Typography>
          <ReviewInput
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            secondsRemaining={secondsRemaining}
            fetchingLimit={fetchingLimit}
          />
        </Box>
      )}
    </Box>
  );
};